import { getActiveOtelTrace } from '@pkg/shared/trace'
import type { z } from 'zod'
import { AppError, type AppErrorOptions, isAppError } from './AppError'

export type ErrorMeta = {
  description?: string
  defaultStatus?: number
  severity?: 'info' | 'warn' | 'error'
  contextSchema?: z.ZodTypeAny
}

export type ErrorRegistry = Record<string, ErrorMeta>

export function defineErrors<const T extends ErrorRegistry>(defs: T) {
  return defs
}

// Base/shared keys used across the monorepo
export const sharedErrors = defineErrors({
  VALIDATION: { defaultStatus: 422, severity: 'warn' },
  INTERNAL: { defaultStatus: 500, severity: 'error' },
  SERVICE_UNAVAILABLE: { defaultStatus: 503, severity: 'info' },
  UNAUTHORIZED: { defaultStatus: 401, severity: 'warn' },
  NO_PERMISSION: { defaultStatus: 403, severity: 'warn' },
  VALIDATION_FAILED: { defaultStatus: 422, severity: 'warn' },
  INPUT_VALIDATION_FAILED: { defaultStatus: 422, severity: 'warn' },
  OUTPUT_VALIDATION_FAILED: { defaultStatus: 500, severity: 'error' },
  RESOURCE_NOT_FOUND: { defaultStatus: 404, severity: 'info' },
  NOT_FOUND: { defaultStatus: 404, severity: 'info' },
  UPGRADE_REQUIRED: { defaultStatus: 402, severity: 'warn' },
  ABORTED: { defaultStatus: 499, severity: 'info' },
} as const)

/**
 * Define an app-scoped error registry and get back a typed `appError` factory
 * that understands the registry's keys and applies default status from metadata.
 *
 * Merges the provided definitions with `sharedErrors` by default.
 */
export function defineErrorRegistry<const T extends ErrorRegistry>(
  registry: string,
  defs: T,
  options?: { base?: ErrorRegistry; defaultKey?: string },
) {
  const base = options?.base ?? sharedErrors
  const merged = { ...base, ...defs } as const

  type Keys = keyof typeof merged & string

  // Internal utility to consistently merge contexts in ensure/augment flows
  function mergeContexts<C>(existing: unknown, incoming: unknown, doMerge: boolean): C | undefined {
    if (!doMerge) return (incoming as C | undefined) ?? (existing as C | undefined)
    const a = existing
    const b = incoming
    if (a && typeof a === 'object' && b && typeof b === 'object') {
      return { ...a, ...b } as C
    }
    return (b as C | undefined) ?? (a as C | undefined)
  }

  function appScopedAppError<C = unknown>(
    key: Keys,
    optOrMessage?: Omit<AppErrorOptions<C>, 'registry'> | string,
    orOpts?: Omit<AppErrorOptions<C>, 'registry'>,
  ) {
    const opts = typeof optOrMessage === 'string' ? { message: optOrMessage } : (optOrMessage ?? orOpts ?? {})
    const meta = merged[key]
    const status = opts.status ?? meta?.defaultStatus
    return new AppError<Keys, C>(key, {
      ...opts,
      ...getActiveOtelTrace(),
      registry,
      status,
    })
  }

  // Build a typed factory and attach helpful metadata for DX and tooling
  const factory = ((key: string, opts?: AppErrorOptions<unknown>) =>
    appScopedAppError(key as Keys, opts as AppErrorOptions)) as <K extends Keys, C = unknown>(
    key: K,
    opts?: AppErrorOptions<C>,
  ) => AppError<K, C>

  const keysArr = Object.keys(merged) as Keys[]
  const getMeta = (key: Keys) => merged[key]
  const isKey = (key: unknown): key is Keys => typeof key === 'string' && (keysArr as string[]).includes(key)

  // Default key to use when wrapping unknown errors
  const defaultKey: Keys = (isKey(options?.defaultKey) ? options?.defaultKey : 'INTERNAL') as Keys

  type EnsureOptions<C> = Omit<AppErrorOptions<C>, 'registry'> & {
    key?: Keys
    /** If both contexts are objects, perform a shallow merge (existing context first). Defaults to true. */
    mergeContext?: boolean
  }

  function ensure<C = unknown>(err: unknown, opts?: EnsureOptions<C>): AppError<Keys, C> {
    const key = opts?.key && isKey(opts.key) ? opts.key : defaultKey
    const meta = merged[key]

    if (isAppError(err)) {
      if (!opts) {
        return err as AppError<Keys, C>
      }
      const mergeContext = opts.mergeContext ?? true
      const next: AppErrorOptions<C> = {
        // Use overrides when provided, otherwise keep original
        message: err.message ?? opts.message,
        status: err.status ?? opts.status ?? meta?.defaultStatus,
        data: mergeContexts<C>(err.data, opts.data, mergeContext),
        // Preserve original error chain; explicit cause can override
        cause: (err as Error).cause ?? opts.cause,
        ...getActiveOtelTrace(),
      }
      return new AppError<Keys, C>(err.code as Keys, { ...next, registry })
    }

    const next: AppErrorOptions<C> = {
      message: opts?.message,
      status: opts?.status ?? meta?.defaultStatus,
      data: opts?.data,
      cause: (opts?.cause as Error | undefined) ?? (err as Error | undefined),
      ...getActiveOtelTrace(),
    }
    return new AppError<Keys, C>(key, { ...next, registry, cause: opts?.cause ?? err })
  }

  function rethrow<C = unknown>(err: unknown, opts?: EnsureOptions<C>): never {
    throw ensure<C>(err, opts)
  }

  type AugmentOptions<C> = Omit<AppErrorOptions<C>, 'registry' | 'status'> & {
    /** If both contexts are objects, perform a shallow merge (existing context first). Defaults to true. */
    mergeContext?: boolean
  }

  /**
   * Attach or enrich context/message/cause on an error while keeping its key and status.
   * - If `err` is an AppError: returns a new AppError with the same key and status.
   * - Otherwise: normalizes via `ensure(err, opts)` using the registry defaultKey and metadata.
   */
  function augment<C = unknown>(err: unknown, opts?: AugmentOptions<C>): AppError<Keys, C> {
    if (isAppError(err)) {
      const mergeContext = opts?.mergeContext ?? true
      const next: AppErrorOptions<C> = {
        message: opts?.message ?? err.message,
        status: err.status, // preserve status
        data: mergeContexts<C>(err.data, opts?.data, mergeContext),
        cause: opts?.cause ?? (err as Error).cause,
        ...getActiveOtelTrace(),
      }
      return new AppError<Keys, C>(err.code as Keys, { ...next, registry })
    }
    // not an AppError → normalize and enrich using ensure (will apply defaultKey/defaultStatus)
    return ensure<C>(err, { ...(opts as Omit<EnsureOptions<C>, 'key' | 'status'>) })
  }

  /**
   * Assert an invariant and throw an AppError if it fails.
   * Uses provided key and options (defaults status from registry metadata when omitted).
   */
  function assert<C = unknown>(
    condition: unknown,
    key: Keys,
    opts?: Omit<AppErrorOptions<C>, 'registry'>,
  ): asserts condition {
    if (condition) return
    // Call through the app-scoped factory to apply defaultStatus, registry tagging, etc.
    throw appScopedAppError<C>(key, opts as AppErrorOptions<C>)
  }

  function tryOrThrow<T>(fn: () => T, mapErr: (err: unknown) => Error): T
  function tryOrThrow<T>(fn: () => Promise<T>, mapErr: (err: unknown) => Error): Promise<T>
  function tryOrThrow<T>(fn: () => T | Promise<T>, mapErr: (err: unknown) => Error): T | Promise<T> {
    let result: T | Promise<T>
    try {
      result = fn()
    } catch (err) {
      throw mapErr(err)
    }
    if (result && typeof (result as Promise<T>).then === 'function') {
      return (result as Promise<T>).catch((err: unknown) => {
        throw mapErr(err)
      })
    }
    return result
  }

  function tryAs<T>(code: Keys, message: string, fn: () => T): T
  function tryAs<T>(code: Keys, message: string, fn: () => Promise<T>): Promise<T>
  function tryAs<T>(code: Keys, message: string, fn: () => T | Promise<T>): T | Promise<T> {
    return tryOrThrow(fn as () => T, err => ensure(err, { key: code, message }))
  }

  return Object.assign(factory, {
    keys: keysArr as ReadonlyArray<Keys>,
    registry: merged,
    getMeta,
    isKey,
    defaultKey,
    ensure,
    rethrow,
    augment,
    assert,
    tryOrThrow,
    tryAs,
  }) as (<K extends Keys, C = unknown>(
    key: K,
    optOrMessage?: Omit<AppErrorOptions<C>, 'registry'> | string,
    orOpts?: Omit<AppErrorOptions<C>, 'registry'>,
  ) => AppError<K, C>) & {
    keys: ReadonlyArray<Keys>
    registry: typeof merged
    getMeta: (key: Keys) => ErrorMeta | undefined
    isKey: (key: unknown) => key is Keys
    defaultKey: Keys
    ensure: <C = unknown>(err: unknown, opts?: EnsureOptions<C>) => AppError<Keys, C>
    rethrow: <C = unknown>(err: unknown, opts?: EnsureOptions<C>) => never
    augment: <C = unknown>(err: unknown, opts?: AugmentOptions<C>) => AppError<Keys, C>
    assert: <C = unknown>(
      condition: unknown,
      key: Keys,
      opts?: Omit<AppErrorOptions<C>, 'registry'>,
    ) => asserts condition
    tryOrThrow: {
      <T>(fn: () => T, mapErr: (err: unknown) => Error): T
      <T>(fn: () => Promise<T>, mapErr: (err: unknown) => Error): Promise<T>
    }
    tryAs: {
      <T>(code: Keys, message: string, fn: () => T): T
      <T>(code: Keys, message: string, fn: () => Promise<T>): Promise<T>
    }
  }
}
